import * as TagsInputPrimitive from "@diceui/tags-input";
import * as React from "react";
import { IconX } from "@tabler/icons-react";
import { cn } from "../../utils";

/**
 * A utility function to compose multiple refs into a single ref.
 */
function composeRefs<T>(...refs: React.Ref<T>[]) {
  return (node: T) => {
    for (const ref of refs) {
      if (typeof ref === "function") {
        ref(node);
      } else if (ref != null) {
        (ref as React.MutableRefObject<T>).current = node;
      }
    }
  };
}

interface TagsInputContextValue {
  onInputChange?: (value: string) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Context for sharing state and handlers between TagsInput and its subcomponents.
 */
const TagsInputContext = React.createContext<TagsInputContextValue | null>(null);

const useTagsInputContext = () => {
  const context = React.useContext(TagsInputContext);
  if (!context) {
    throw new Error("useTagsInputContext must be used within a TagsInput component");
  }
  return context;
};

export interface TagsInputProps extends React.ComponentPropsWithoutRef<typeof TagsInputPrimitive.Root> {
  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
}

const TagsInput = React.forwardRef<HTMLDivElement, TagsInputProps>(
  (
    { className, suggestions = [], children, onSuggestionSelect, value, onValueChange, ...rest },
    ref
  ) => {
    const [isDropdownOpen, setDropdownOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState("");
    const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    // Effect to clear the input when a tag is added via the underlying component.
    const prevValueRef = React.useRef(value);
    React.useEffect(() => {
      if (value && prevValueRef.current && value.length > prevValueRef.current.length) {
        setInputValue("");
      }
      prevValueRef.current = value;
    }, [value]);

    // Memoize the filtered suggestions to avoid re-calculating on every render.
    const filteredSuggestions = React.useMemo(() => {
      const selectedTags = Array.isArray(value) ? value : [];
      const availableSuggestions = suggestions.filter(
        suggestion => !selectedTags.includes(suggestion)
      );

      if (inputValue) {
        const lowercasedValue = inputValue.toLowerCase();
        return availableSuggestions.filter(suggestion =>
          suggestion.toLowerCase().includes(lowercasedValue)
        );
      }
      return availableSuggestions;
    }, [value, inputValue, suggestions]);

    // Effect to auto-highlight the suggestion if it's the only one.
    React.useEffect(() => {
      if (filteredSuggestions.length === 1 && inputValue) {
        setHighlightedIndex(0);
      } else {
        setHighlightedIndex(-1);
      }
    }, [filteredSuggestions, inputValue]);

    // Effect to scroll the highlighted item into view.
    React.useEffect(() => {
      if (highlightedIndex === -1) return;

      const dropdownElement = dropdownRef.current;
      if (dropdownElement) {
        const scrollableContainer = dropdownElement.children[0];
        const highlightedElement = scrollableContainer.children[highlightedIndex] as HTMLElement;
        if (highlightedElement) {
          highlightedElement.scrollIntoView({
            block: "nearest",
          });
        }
      }
    }, [highlightedIndex]);

    const handleInputChange = (currentValue: string) => {
      setInputValue(currentValue);
      if (suggestions.length > 0) {
        setDropdownOpen(true);
      }
    };

    const handleFocus = () => {
      if (suggestions.length > 0) {
        setDropdownOpen(true);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Prevent closing the dropdown if the focus is moved to an element within the dropdown.
      if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
        return;
      }
      setTimeout(() => {
        setDropdownOpen(false);
      }, 200);
    };

    const handleSuggestionClick = (suggestion: string) => {
      if (onSuggestionSelect) {
        onSuggestionSelect(suggestion);
      }
      setInputValue("");
      if (inputRef.current) {
        inputRef.current.value = "";
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isDropdownOpen) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setHighlightedIndex(prev => (prev + 1) % filteredSuggestions.length);
          break;
        case "ArrowUp":
          e.preventDefault();
          setHighlightedIndex(
            prev => (prev - 1 + filteredSuggestions.length) % filteredSuggestions.length
          );
          break;
        case "Enter":
          if (highlightedIndex !== -1) {
            e.preventDefault();
            handleSuggestionClick(filteredSuggestions[highlightedIndex]);
          }
          break;
        case "Escape":
          setDropdownOpen(false);
          break;
      }
    };

    const renderChildren = (diceUIContext: { value: string[] }) => {
      if (typeof children === "function") {
        return children(diceUIContext);
      }
      return children;
    };

    return (
      <TagsInputPrimitive.Root
        data-slot="tags-input"
        ref={ref}
        className={cn("al-relative al-flex al-w-[380px] al-flex-col al-gap-2", className)}
        {...rest}
        value={value}
        onValueChange={onValueChange}
      >
        {diceUIContext => (
          <>
            <TagsInputContext.Provider
              value={{
                onInputChange: handleInputChange,
                onFocus: handleFocus,
                onBlur: handleBlur,
                onKeyDown: handleKeyDown,
                inputRef: inputRef,
              }}
            >
              {renderChildren(diceUIContext)}
            </TagsInputContext.Provider>
            {isDropdownOpen && (filteredSuggestions.length > 0 || inputValue) && (
              <div
                ref={dropdownRef}
                data-slot="tags-input-dropdown"
                className="al-absolute al-top-full al-z-10 al-mt-1 al-w-full al-rounded-md al-border al-bg-background al-shadow-lg"
              >
                <div className="al-max-h-[300px] al-overflow-y-auto al-overflow-x-hidden al-p-2">
                  {filteredSuggestions.length > 0 ? (
                    filteredSuggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        role="option"
                        aria-selected={highlightedIndex === index}
                        data-selected={highlightedIndex === index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className={cn(
                          "al-relative al-flex al-cursor-pointer al-gap-2 al-select-none al-items-center al-rounded-sm al-px-2 al-py-1.5 al-text-sm al-outline-none",
                          highlightedIndex === index && "al-bg-accent al-text-accent-foreground"
                        )}
                      >
                        {suggestion}
                      </div>
                    ))
                  ) : (
                    <div className="al-py-6 al-text-center al-text-sm">
                      Press Enter to add &#34;{inputValue}&#34;
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </TagsInputPrimitive.Root>
    );
  }
);
TagsInput.displayName = TagsInputPrimitive.Root.displayName;

const TagsInputLabel = React.forwardRef<
  HTMLLabelElement,
  React.ComponentPropsWithoutRef<typeof TagsInputPrimitive.Label>
>(({ className, ...props }, ref) => (
  <TagsInputPrimitive.Label
    data-slot="tags-input-label"
    ref={ref}
    className={cn(
      "al-font-medium al-text-sm al-leading-none peer-disabled:al-cursor-not-allowed peer-disabled:al-opacity-70",
      className
    )}
    {...props}
  />
));
TagsInputLabel.displayName = TagsInputPrimitive.Label.displayName;

const TagsInputList = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      data-slot="tags-input-list"
      ref={ref}
      className={cn(
        "al-flex al-min-h-10 al-w-full al-flex-wrap al-items-center al-gap-1.5 al-rounded-md al-border al-border-input al-bg-background al-px-3 al-py-2 al-text-sm",
        "focus-within:al-outline-none focus-within:al-ring-2 focus-within:al-ring-ring focus-within:al-ring-offset-2",
        "disabled:al-cursor-not-allowed disabled:al-opacity-50",
        className
      )}
      {...props}
    />
  )
);
TagsInputList.displayName = "TagsInputList";

const TagsInputInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentPropsWithoutRef<typeof TagsInputPrimitive.Input>
>(({ className, onChange, onFocus, onBlur, onKeyDown, ...props }, ref) => {
  const context = useTagsInputContext();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    if (context.onInputChange) {
      context.onInputChange(e.target.value);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onFocus) {
      onFocus(e);
    }
    if (context.onFocus) {
      context.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (onBlur) {
      onBlur(e);
    }
    if (context.onBlur) {
      context.onBlur(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (onKeyDown) {
      onKeyDown(e);
    }
    if (context.onKeyDown) {
      context.onKeyDown(e);
    }
  };

  return (
    <TagsInputPrimitive.Input
      data-slot="tags-input-input"
      ref={composeRefs(ref, context.inputRef)}
      className={cn(
        "al-flex-1 al-bg-transparent al-outline-none",
        "placeholder:al-text-muted-foreground",
        "disabled:al-cursor-not-allowed disabled:al-opacity-50",
        "focus:al-outline-none focus:al-ring-0 focus-visible:al-ring-0 focus-visible:al-ring-offset-0",
        "al-border-none",
        className
      )}
      {...props}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
});
TagsInputInput.displayName = TagsInputPrimitive.Input.displayName;

const TagsInputItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof TagsInputPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <TagsInputPrimitive.Item
    data-slot="tags-input-item"
    ref={ref}
    className={cn(
      "al-inline-flex al-max-w-[calc(100%-8px)] al-items-center al-gap-1.5 al-rounded al-border al-bg-transparent al-px-2 al-py-1 al-text-sm focus:al-outline-hidden data-disabled:al-cursor-not-allowed data-editable:al-select-none data-editing:al-bg-transparent data-disabled:al-opacity-50 data-editing:al-ring-1 data-editing:al-ring-ring [&:not([data-editing])]:al-pr-1.5 [&[data-highlighted]:not([data-editing])]:al-bg-accent [&[data-highlighted]:not([data-editing])]:al-text-accent-foreground",
      className
    )}
    {...props}
  >
    <TagsInputPrimitive.ItemText className="truncate">{children}</TagsInputPrimitive.ItemText>
    <TagsInputPrimitive.ItemDelete className="al-h-4 al-w-4 al-shrink-0 al-rounded-sm al-opacity-70 al-ring-offset-background al-transition-opacity hover:al-opacity-100">
      <IconX className="al-h-3.5 al-w-3.5" />
    </TagsInputPrimitive.ItemDelete>
  </TagsInputPrimitive.Item>
));
TagsInputItem.displayName = TagsInputPrimitive.Item.displayName;

const TagsInputClear = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof TagsInputPrimitive.Clear>
>(({ ...props }, ref) => (
  <TagsInputPrimitive.Clear data-slot="tags-input-clear" ref={ref} {...props} />
));
TagsInputClear.displayName = TagsInputPrimitive.Clear.displayName;

export { TagsInput, TagsInputLabel, TagsInputList, TagsInputInput, TagsInputItem, TagsInputClear };
